import React from "react";
import { useTranslation } from "react-i18next";
import {
  useRetrieveConversations,
  useGetConversationMessages,
  useResetMemory,
  useChatStream,
} from "@planzone/view-models";
import { ChatMessage } from "@planzone/dto";
import { UseCases } from "@planzone/core";
import { useModal } from "@root/client/modal";
import { useMessage } from "@components/useMessage";
import { events } from "@planzone/events";
import { ConversationSidebar } from "./conversationSidebar";
import { Messages } from "./messages";
import { ChatInputArea } from "./chatInputArea";
import assistantIcon from "@scenes/assistantShortcut/icon.png";
import styles from "./styles.scss";

function Chatbot() {
  const { t } = useTranslation();
  const [selectedConversationId, setSelectedConversationId] = React.useState<
    string | undefined
  >(undefined);

  const {
    conversations,
    isRequestPending: isConversationsPending,
    isRequestSuccess: isConversationsSuccess,
    isRequestFailure: isConversationsFailure,
    retrieveConversations,
  } = useRetrieveConversations();

  const {
    messages: historyMessages,
    isRequestPending: isHistoryPending,
    getConversationMessages,
  } = useGetConversationMessages();

  const { resetMemory } = useResetMemory();
  const { pushView } = useModal();

  const {
    messages: streamMessages,
    isSubmitPending,
    isStreaming,
    statusMessage,
    streamError,
    sendMessage,
    cancelJob,
  } = useChatStream(selectedConversationId);

  React.useEffect(() => {
    !isConversationsPending &&
      !isConversationsSuccess &&
      !isConversationsFailure &&
      retrieveConversations();
  }, []);

  React.useEffect(() => {
    !!selectedConversationId &&
      getConversationMessages({ conversationId: selectedConversationId });
  }, [selectedConversationId]);

  useMessage(({ type, data }) => {
    if (
      type === events.deleteConversationSucceeded &&
      data.conversationId === selectedConversationId
    ) {
      setSelectedConversationId(undefined);
    }
  });

  // Background reply notification: mirrors the standalone assistant app —
  // no dedicated `notifications` domain wiring for this proto iteration
  // (see ai-assistant-integration.md, "Décisions").
  const wasStreamingRef = React.useRef(false);
  const originalTitleRef = React.useRef(document.title);

  React.useEffect(() => {
    if (typeof Notification === "undefined") return;
    if (Notification.permission === "default") {
      Notification.requestPermission();
    }
  }, []);

  React.useEffect(() => {
    if (wasStreamingRef.current && !isStreaming && document.hidden) {
      document.title = t("chatbot.notification.title");
      if (
        typeof Notification !== "undefined" &&
        Notification.permission === "granted"
      ) {
        new Notification(t("chatbot.notification.title"), {
          body: t("chatbot.notification.body"),
        });
      }
    }
    wasStreamingRef.current = isStreaming;
  }, [isStreaming]);

  React.useEffect(() => {
    const restoreTitle = () => {
      if (!document.hidden) document.title = originalTitleRef.current;
    };
    document.addEventListener("visibilitychange", restoreTitle);
    return () => document.removeEventListener("visibilitychange", restoreTitle);
  }, []);

  const messages: ChatMessage[] = selectedConversationId
    ? [...historyMessages, ...streamMessages]
    : streamMessages;

  return (
    <div className={styles.chatbot}>
      <ConversationSidebar
        conversations={conversations}
        selectedConversationId={selectedConversationId}
        isPending={isConversationsPending}
        select={setSelectedConversationId}
        createNew={() => setSelectedConversationId(undefined)}
        remove={(conversation) =>
          pushView({ key: UseCases.deleteConversation, data: conversation })
        }
        resetMemory={(conversation) =>
          resetMemory({ conversationId: conversation.uuid })
        }
      />
      <div className={styles.conversation}>
        <div className={styles.header}>
          <img src={assistantIcon} className={styles.headerIcon} alt="" />
          <h2 className={styles.headerTitle}>{t("chatbot.header.title")}</h2>
        </div>
        <Messages
          messages={messages}
          isPending={!!selectedConversationId && isHistoryPending}
          isStreaming={isStreaming}
          statusMessage={statusMessage}
        />
        {streamError !== undefined && (
          <p className={styles.error}>{t("chatbot.error")}</p>
        )}
        <ChatInputArea
          isStreaming={isStreaming}
          isDisabled={isSubmitPending}
          send={sendMessage}
          cancel={cancelJob}
        />
      </div>
    </div>
  );
}

export { Chatbot };
